import React from 'react';
import { FormControlLabel, Radio, RadioGroup, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Styled Radio component with orange theme
 */
const StyledRadio = styled(Radio)(({ theme }) => ({
  color: '#9e9e9e',
  '&.Mui-checked': {
    color: '#ff9800',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 24,
  },
}));

/**
 * Styled container for the radio group
 */
const RadioContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

/**
 * HavcRadioOption - Reusable radio button group component for HAVC forms
 * 
 * @param {Object} props
 * @param {string} props.name - The name attribute for the radio group
 * @param {boolean} props.value - Current selected value (true = draft, false = create receipts)
 * @param {function} props.onChange - Handler called when selection changes
 * @param {boolean} props.disabled - Whether the radio group is disabled
 * @param {string} props.draftLabel - Label for draft option (default: "Taslak Olarak Kaydet")
 * @param {string} props.createLabel - Label for create receipts option (default: "Fişleri Oluştur")
 * @param {string} props.size - Size of the radio buttons ("small" | "medium")
 */
const HavcRadioOption = ({
  name = 'DraftProject',
  value,
  onChange,
  disabled = false,
  draftLabel = 'Taslak Olarak Kaydet',
  createLabel = 'Fişleri Oluştur',
  size = 'medium',
}) => {
  const handleChange = (event) => {
    if (onChange) {
      // Create a synthetic event matching the expected format
      const syntheticEvent = {
        target: {
          name: name,
          type: 'checkbox',
          checked: event.target.value === 'draft',
        },
      };
      onChange(syntheticEvent);
    }
  };

  const currentValue = value ? 'draft' : 'create';

  return (
    <RadioContainer>
      <RadioGroup
        row
        name={name}
        value={currentValue}
        onChange={handleChange}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FormControlLabel
          value="draft"
          control={
            <StyledRadio
              size={size}
              disabled={disabled}
            />
          }
          label={
            <Typography
              variant="body1"
              sx={{
                fontWeight: value ? 600 : 400,
                color: value ? '#ff9800' : 'inherit',
              }}
            >
              {draftLabel}
            </Typography>
          }
          disabled={disabled}
          sx={{
            marginRight: 2,
            '& .MuiFormControlLabel-label': {
              marginLeft: 0.5,
            },
          }}
        />
        <FormControlLabel
          value="create"
          control={
            <StyledRadio
              size={size}
              disabled={disabled}
            />
          }
          label={
            <Typography
              variant="body1"
              sx={{
                fontWeight: !value ? 600 : 400,
                color: !value ? '#ff9800' : 'inherit',
              }}
            >
              {createLabel}
            </Typography>
          }
          disabled={disabled}
          sx={{
            '& .MuiFormControlLabel-label': {
              marginLeft: 0.5,
            },
          }}
        />
      </RadioGroup>
    </RadioContainer>
  );
};

export default HavcRadioOption;
